// Per-vertical copy overrides (Sprint 2.2 + 2.3).
//
// Tightens each vertical's hero + proof + CTA into that buyer's vocabulary.
// Verb-led, sentence case, no emoji, one brand voice. Follows the CLAUDE.md
// voice rules (no enumerated transitions, em-dash cap).
//
// CONTENT INTEGRITY: every proof figure here is lifted verbatim from the
// vertical's existing `stats` / `benchmark` in lib/copy/industries-detail.ts
// (which are themselves consolidated public-source ranges or flagged
// composites). No new numbers are introduced. Where a vertical has no
// tightening entry, the registry falls back to its industries-detail copy
// and stats automatically.

import type { ProofPoint, VerticalCta } from './types'

/** Tightened hero copy per vertical. Only override where the buyer vocabulary
 *  needs sharpening; absent slugs use the industries-detail hero as-is. */
export const VERTICAL_HERO_TIGHTEN: Record<string, { headline: string; subhead: string }> = {
  'travel-and-experiences': {
    headline: 'The traveller’s reel books the trip. Your brochure rarely does.',
    subhead:
      'Travel buyers shop for a feeling they want to recreate. Surface the destination reels, itinerary recaps and packing lists your past customers already posted, tagged to the exact tour and date, so the next booker arrives half-sold.',
  },
  'beauty-and-cosmetics': {
    headline: 'Shoppers add to cart when they see it work on skin like theirs.',
    subhead:
      'Shade, finish and texture never read from a studio shot. Surface real routines, swatches and before/after clips from customers with the same skin, tagged to the step that matters, so they buy from the moment that convinced them.',
  },
  furniture: {
    headline: 'See the sofa in a real living room before you commit the AOV.',
    subhead:
      'Furniture is bought once and lived with for years, so the conviction gap on a high-AOV order is wide. Show the piece across real customer rooms and angles, with delivery and assembly answered by the people who already own it.',
  },
  'consumer-electronics': {
    headline: 'Tech buyers trust the reviewer’s unboxing over your spec sheet.',
    subhead:
      'Electronics buyers research hard before they commit. Run reviewer videos, setup walkthroughs and use-case demos with chapter-aware tagging, so a shopper jumps to the spec moment that matters and checks out from the clip.',
  },
  'sporting-goods': {
    headline: 'Athletes buy the kit they see other athletes train in.',
    subhead:
      'Performance gear sells on proof of use. Run athlete content, gear-in-action loops and program-graduate transformations on the PDP, so the next buyer sees your kit survive the same session they’re training for.',
  },
  hospitality: {
    headline: 'Guests believe other guests, not your hero shot.',
    subhead:
      'Travellers trust travellers. Surface guest photos and reviews from Instagram, TripAdvisor and Google against the room, the dish and the venue, so a booker sees the real stay before the glossy one.',
  },
  'accessories-and-jewelery': {
    headline: 'See how a piece sits on a real wrist before you buy.',
    subhead:
      'Scale, weight and how a piece catches the light never read from a studio crop. Put on-body customer photos above the size selector, so the single biggest online-to-in-store gap closes at the point of decision.',
  },
  'fashion-and-apparels': {
    headline: 'Real outfits, real bodies, fewer returns.',
    subhead:
      'Fit is the hardest thing to sell from a studio shot and the top reason fashion comes back. Tag your customers’ fit-checks and try-on hauls straight to the product, so a shopper sees the cut on a body like theirs before they buy.',
  },
}

/** Tightened 3-point proof per vertical, in buyer vocabulary. Figures are
 *  lifted from the vertical's industries-detail `stats` / `benchmark`. */
export const VERTICAL_PROOF: Record<string, ProofPoint[]> = {
  'travel-and-experiences': [
    {
      stat: '+41%',
      label: 'booking conversion on tour PDPs with a traveller reel + packing block',
    },
    { stat: '2.8×', label: 'destination-page time on page with a UGC wall vs hero photo only' },
    {
      stat: '54%',
      label: 'of travel shoppers cite social UGC as their top inspiration (Mintel, 2025)',
    },
  ],
  'beauty-and-cosmetics': [
    { stat: '2-3×', label: 'PDP conversion when real-skin review UGC leads the page' },
    { stat: '+28%', label: 'repeat purchase when ingredient stories surface on the PDP' },
    { stat: '−21%', label: 'returns when real-skin demos set fit and finish expectations' },
  ],
  furniture: [
    { stat: '+27%', label: 'PDP dwell time when a customer-room photo replaces a studio shot' },
    { stat: '$651K', label: 'attributed gross sales at Leroy Merlin (GA4, 10 months)' },
    { stat: 'Zero', label: 'site-speed impact from the lazy-loaded widget runtime' },
  ],
  'fashion-and-apparels': [
    { stat: '5×', label: 'conversion rate when UGC replaces studio imagery' },
    { stat: '+34%', label: 'average order value with fit-check UGC on the PDP (n=46 brands)' },
    { stat: '80%', label: 'less manual content review with AI moderation' },
  ],
  'consumer-electronics': [
    { stat: '+19%', label: 'PDP dwell time when video reviews replace static spec lists' },
    { stat: '−18%', label: 'support tickets when setup walkthroughs sit on the product page' },
    { stat: 'Zero', label: 'site-speed impact from the lazy-loaded widget runtime' },
  ],
  'sporting-goods': [
    { stat: '+33%', label: 'conversion on training-program pages with athlete UGC (n=9 brands)' },
    { stat: 'Live', label: 'race + event walls during sponsored sessions' },
    { stat: 'Zero', label: 'site-speed impact from the lazy-loaded widget runtime' },
  ],
  hospitality: [
    { stat: '+47%', label: 'engagement on destination pages with traveller UGC (n=12 properties)' },
    { stat: '4K', label: 'real-time hashtag walls on lobby + venue signage' },
    { stat: 'Multi', label: 'source feed from Instagram, TripAdvisor and Google in one widget' },
  ],
}

/** Cobalt CTA labels per vertical: outcome verb + vertical object. */
export const VERTICAL_CTA: Record<string, VerticalCta> = {
  'travel-and-experiences': {
    primaryLabel: 'Build your travel wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'beauty-and-cosmetics': {
    primaryLabel: 'Build your beauty wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  furniture: {
    primaryLabel: 'Build your room-set wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'fashion-and-apparels': {
    primaryLabel: 'Book a demo',
    secondaryLabel: 'Talk to sales',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'consumer-electronics': {
    primaryLabel: 'Build your reviewer wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'sporting-goods': {
    primaryLabel: 'Build your athlete wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  hospitality: {
    primaryLabel: 'Build your guest wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'accessories-and-jewelery': {
    primaryLabel: 'Build your on-body wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'home-lifestyle': {
    primaryLabel: 'Build your real-homes wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'food-and-beverage': {
    primaryLabel: 'Build your recipe wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'health-and-wellness': {
    primaryLabel: 'Build your routine wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'baby-and-kids': {
    primaryLabel: 'Build your parent-proof wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'pet-care': {
    primaryLabel: 'Build your before-and-after wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  automobiles: {
    primaryLabel: 'Build your owner wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  edtech: {
    primaryLabel: 'Build your outcomes wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
  'b2b-saas': {
    primaryLabel: 'Build your proof wall',
    secondaryLabel: 'See it live',
    reassurance: 'No code · live in a day',
    primaryHref: 'https://app.idukki.io/register',
    secondaryHref: '/contact?intent=demo',
  },
}
